import instance from '../xhrService';
import db from '../../db/db';
import { withOfflineFallback, withOfflineWrite } from '../offlineFallback';
import { random } from 'lodash';

/**
 * Commentables
 */

const url = (a, b = '', c) => `/api/notes/${a}/${b}` + (c ? `/${c}` : '');

const createOfflineCommentable = async (
  modelType,
  modelId,
  data,
  online,
  id,
) => {
  const user = await db.user.get(1);

  if (!user) {
    return;
  }

  const inspection = await db.inspections.get(Number(modelId)).catch((e) => {
    console.log('error', e);
  });

  if (!inspection) {
    return;
  }

  // Create commentable objects if comments exist
  const commentableObj = data.note
    ? {
        author: {
          name: user.name,
          id: user.user_id,
          first_name: user.first_name,
          last_name: user.last_name,
        },
        note: data.note,
        id: id && id !== 0 ? id : random(false, 1000000),
        created_at: new Date().toISOString(),
      }
    : {};

  const notes = await db.inspectionNotes.get(Number(modelId)).catch((e) => {
    console('log', e);
    throw e;
  });

  if (!notes) {
    await db.inspectionNotes.add({
      inspection_id: Number(modelId),
      notes: [commentableObj],
      sync: {
        synced: online ? true : false,
        payload: [
          {
            method: 'create',
            ...commentableObj.note,
            // SW2-2443: the append branch below always carried this; without it
            // the sync could not match a later edit of this note to the id the
            // server gave it, and sent the edit to the local random id instead.
            id: commentableObj.id,
          },
        ],
      },
    });
  } else {
    await db.inspectionNotes.update(Number(modelId), {
      notes: [...notes.notes, commentableObj],
      sync: {
        // SW2-2443: keep `noteIds` — the offline sync's local → server note id
        // map for a queue it has part-sent.
        ...notes.sync,
        synced: online ? true : false,
        payload: [
          ...(notes.sync?.payload || []),
          {
            method: 'create',
            ...commentableObj.note,
            id: commentableObj.id,
          },
        ],
      },
    });
  }

  return commentableObj;
};

const updateOfflineCommentable = async (
  modelType,
  modelId,
  data,
  inspectionId,
  online,
) => {
  const user = await db.user.get(1);

  if (!user) {
    return;
  }

  const inspection = await db.inspections
    .get(Number(inspectionId))
    .catch((e) => {
      console.log('error', e);
    });

  if (!inspection) {
    return;
  }

  // Create commentable objects if comments exist
  const commentableObj = data.content
    ? {
        author: {
          name: user.name,
          id: user.user_id,
          first_name: user.first_name,
          last_name: user.last_name,
        },
        note: { content: data.content },
        id: data.id,
        created_at: new Date().toISOString(),
      }
    : {};

  const notes = await db.inspectionNotes
    .get(Number(inspectionId))
    .catch((e) => {
      console('log', e);
    });

  if (notes) {
    const updatedNotes = notes.notes.map((note) => {
      if (note.id === data.id) {
        return {
          ...note,
          note: {
            ...note.note,
            content: data.content,
          },
        };
      }

      return note;
    });

    await db.inspectionNotes.update(Number(inspectionId), {
      notes: updatedNotes,
      sync: {
        ...notes.sync, // SW2-2443: keep `noteIds`, see createOfflineCommentable
        synced: online ? true : false,
        payload: [
          ...(notes.sync?.payload || []),
          {
            method: 'update',
            ...data,
          },
        ],
      },
    });

    return commentableObj;
  }

  return commentableObj;
};

/**
 * Only commentables on `inspections` have an offline cache (`db.inspectionNotes`).
 * A commentable on anything else — today that is observations, which hang off
 * `findings` — has nowhere to be written offline and nothing that would replay
 * it, so the write cannot be accepted.
 *
 * Thrown rather than returned as a bare Error so a caller can tell "not
 * supported offline", which is worth a specific message to the user, from a
 * genuine failure. `origin/dev` rejected here too, with an untyped Error that no
 * caller handled — so editing an observation offline surfaced as an uncaught
 * runtime error. (SW2-2321)
 */
export class OfflineUnsupportedError extends Error {
  constructor(modelType) {
    super(`Commentables on "${modelType}" cannot be edited offline`);
    this.name = 'OfflineUnsupportedError';
    this.offlineUnsupported = true;
    this.modelType = modelType;
  }
}

const commentableApi = {
  /**
   * Index commentables on the specified morphable model
   *
   * @param {string} modelType commentable_type
   * @param {number} modelId commentable_id
   */
  index: async (modelType, modelId) =>
    // SW2-2321: converted from a `navigator.onLine` pre-check. After an offline
    // reload notes were empty even though they were cached.
    withOfflineFallback(
      () => instance.get(url(modelType, modelId)).then((_) => _.data),
      async () => {
        if (modelType !== 'inspections') {
          // Only inspections are cached; returning null lets the helper surface
          // the request error rather than presenting "no notes" as success.
          return null;
        }

        const data = await db.inspectionNotes.get(Number(modelId));

        return data ? data.notes : null;
      },
    ),
  create: (modelType, modelId, data, opts = {}) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // POST was attempted, failed, and the note was never queued.
    withOfflineWrite(
      async () => {
        const result = await instance
          .post(url(modelType, modelId), data)
          .then((_) => _.data);

        if (modelType === 'inspections') {
          // SW2-2321: skipped on replay — the note is already in
          // `inspectionNotes`, and this appends a second server-backed copy
          // beside it, so every successful replay left a duplicate in the cache.
          // (Raised by Copilot on PR #53.)
          if (!opts.replay) {
            await createOfflineCommentable(
              modelType,
              modelId,
              data,
              true,
              result.id,
            );
          }
        }

        return result;
      },
      () => {
        if (modelType !== 'inspections') {
          throw new OfflineUnsupportedError(modelType);
        }

        return createOfflineCommentable(modelType, modelId, data);
      },
      opts,
    ),
  update: async (modelType, modelId, data, inspectionId, opts = {}) => {
    if (!data.id) {
      return Promise.reject(new Error('`data.id` is required'));
    }

    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // PUT was attempted, failed, and the edit was never queued.
    return withOfflineWrite(
      async () => {
        const result = await instance
          .put(url(modelType, data.id), data)
          .then((_) => _.data);

        if (modelType === 'inspections') {
          // SW2-2321: skipped on replay; the local record is already current.
          if (!opts.replay) {
            await updateOfflineCommentable(
              modelType,
              modelId,
              data,
              inspectionId,
              true,
            );
          }
        }

        return result;
      },
      () => {
        if (modelType !== 'inspections') {
          throw new OfflineUnsupportedError(modelType);
        }

        return updateOfflineCommentable(
          modelType,
          modelId,
          data,
          inspectionId,
          false,
        );
      },
      opts,
    );
  },
  destroy: (id, modelType, modelId) =>
    // SW2-2321: was a `navigator.onLine` pre-check. After an offline reload the
    // DELETE was attempted, failed, and the note stayed put.
    withOfflineWrite(
      () => instance.delete(url(modelType, id)).then(() => true),
      async () => {
        if (modelType !== 'inspections') {
          throw new OfflineUnsupportedError(modelType);
        }

        // The previous offline branch read `db.inspectionNotes.notes` — a
        // property of the Dexie *table*, not of a record — so this threw a
        // TypeError. It was unreachable while the pre-check swallowed it; making
        // the path reachable means fixing it. Read the record, then filter.
        const record = await db.inspectionNotes.get(Number(modelId));

        if (!record) {
          throw new Error('Inspection notes not cached offline');
        }

        await db.inspectionNotes.update(Number(modelId), {
          notes: (record.notes || []).filter((note) => note.id !== id),
        });

        return true;
      },
      // SW2-2321: `localOnlyWhenOffline` because nothing replays this local
      // write — the sync loop handles only create/update payloads. Resolving on
      // an unreachable server would claim the deletion will sync when it never
      // will, and the note would come back on the next download.
      // (Raised by Copilot on PR #53.)
      { localOnlyWhenOffline: true },
    ),
};

export default commentableApi;
